/**
 * Drives the DS dark theme.
 *
 * `data-theme` on the document is what the `[data-theme="dark"]` block in
 * theme.scss keys off — with nothing writing it the whole dark palette is
 * unreachable. In the app the sole writer is `applyStoredTheme` at boot; this
 * hook writes it as well, but no app code mounts the hook. Storybook writes it
 * independently, from the theme toolbar global in its preview decorator.
 * (`DActionPanel` carries a literal `data-theme="dark"` too, but that scopes a
 * single subtree to the dark palette and never touches the document.)
 *
 * Nothing renders DThemeToggle on purpose: theme.scss still marks the dark
 * palette as an estimate pending DS validation, so the toggle stays out of the
 * toolbar until design signs those colours off — mounting it is one line then.
 * Until that day a dead-code sweep will read all of this as unused; it is
 * parked capability, and deleting it removes dark mode.
 */
import { useCallback, useEffect, useState } from "react";

export type ThemeMode = "light" | "dark";

const STORAGE_KEY = "d-theme";

/**
 * Touching `localStorage` throws outright wherever site data is blocked (Safari
 * private mode, restricted third-party storage, cookies disabled). This one runs
 * at module scope during boot, so an unguarded throw would white-screen the app
 * over a colour preference.
 */
function readStoredTheme(): ThemeMode {
  if (typeof window === "undefined") return "light";
  try {
    // Dark is opt-in — it is reached by pressing the toggle, never by inheriting
    // `prefers-color-scheme`, so anything but a stored "dark" means light.
    return localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

/** Writes throw on the same browsers as reads, and additionally over quota. */
function writeStoredTheme(theme: ThemeMode) {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // The switch still applies to this session, it just will not outlive it.
  }
}

function applyTheme(theme: ThemeMode) {
  document.documentElement.setAttribute("data-theme", theme);
}

/**
 * Puts the persisted choice on the document at boot, so it applies wherever the
 * app runs rather than only where a toggle happens to be mounted. Running before
 * the first render also keeps the app from painting the wrong theme and
 * correcting it.
 */
export function applyStoredTheme() {
  applyTheme(readStoredTheme());
}

export default function useTheme() {
  const [theme, setThemeState] = useState<ThemeMode>(readStoredTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((mode: ThemeMode) => {
    writeStoredTheme(mode);
    setThemeState(mode);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === "light" ? "dark" : "light");
  }, [theme, setTheme]);

  return { theme, setTheme, toggleTheme };
}
